'use client';
import { useAskInputInstance } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useAskInputInstance.js';
import { QUERY_SUBMIT_DEBOUNCE_MS } from '#pplx/frontend/apps/ask/ask-input/ask-input-types/index.js';
import { buildSlashCommandDisplayMetadata } from '#pplx/frontend/apps/ask/commands/buildSlashCommandDisplayMetadata.js';
import { useAvailableSlashCommands } from '#pplx/frontend/apps/ask/commands/hooks/useAvailableSlashCommands.js';
import { useDeepResearchChipRemovalAnalytics } from '#pplx/frontend/apps/ask/hooks/ask-deep-research-hooks/useDeepResearchChipRemovalAnalytics.js';
import { useRefreshAsiAccessDecisionAfterFreeTurnUse } from '#pplx/frontend/apps/ask/hooks/computer-access-hooks/useAsiAccessDecision.js';
import { useComputerBeforeSubmit } from '#pplx/frontend/apps/ask/hooks/computer-before-submit/useComputerBeforeSubmit.js';
import { markComputerDashboardGraphQLCacheStale } from '#pplx/frontend/apps/ask/hooks/computer-hooks/computerDashboardGraphQLCacheState.js';
import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { useSpaceAnalytics } from '#pplx/frontend/apps/ask/hooks/spaces-hooks/useSpaceAnalytics.js';
import { clearComputerTaskLogicalTiming, markComputerTaskCreated, startComputerTaskLogicalTiming, } from '#pplx/frontend/apps/ask/o11y/computerTaskTiming.js';
import { usePerplexityQueryState } from '#pplx/frontend/apps/ask/providers/ask-query-state-provider/hooks/usePerplexityQueryState.js';
import { useResultsActions } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { hasDeepResearchPrefill } from '#pplx/frontend/ask-models/deepResearchSkill.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { validateUuid } from '#pplx/frontend/common/validateUuid.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { useCallback, useMemo, useRef } from 'react';
export function useAskInput({ collection, workflowConfig, disabled = false, modeConfig, targetThreadAccessLevel, onSubmit, onAcceptedSubmit, redirectToNewThread = true, csrRedirect = false, getThreadUrl, }) {
    const { submitQuery } = usePerplexityQueryState();
    const { clear: clearResults } = useResultsActions();
    const router = useRouter();
    const { handleSubmitQueryWithMention } = useSpaceAnalytics();
    const selectedSearchMode = useSelectedSearchMode();
    const { askDeepResearchModel } = useThreadConfig();
    const routesToComputer = selectedSearchMode === SearchMode.ASI;
    const refreshAccessDecisionAfterFreeTurnUse = useRefreshAsiAccessDecisionAfterFreeTurnUse({
        enabled: routesToComputer,
    });
    const { commands: slashCommands } = useAvailableSlashCommands({ enabled: routesToComputer });
    // Computer access checks always apply; Ask DR defers budget checks to the backend.
    const computerBeforeSubmit = useComputerBeforeSubmit({
        enabled: routesToComputer,
    });
    const handleSubmitQuery = useCallback((params) => {
        const query = params.query?.trim();
        if (!query)
            return false;
        onSubmit?.(params);
        if (params.mentions) {
            if (params.mentions.some((mention) => mention.type === 'space')) {
                handleSubmitQueryWithMention();
            }
        }
        const newFrontendContextUUID = params.newFrontendContextUUID ?? safeRandomUUID();
        const frontend_uuid = safeRandomUUID();
        if (redirectToNewThread) {
            if (routesToComputer) {
                startComputerTaskLogicalTiming({
                    frontendContextUuid: newFrontendContextUUID,
                    querySource: params.querySource,
                });
            }
            // Clear the current thread and stream ids so that we don't
            // have rendered artifacts from the previous thread or stream.
            clearResults();
            const submitted = submitQuery({
                rawQuery: query,
                dslQuery: params.dslQuery,
                mentions: params.mentions,
                prefills: params.prefills,
                attachments: params.attachments,
                queryDisplayMetadata: buildSlashCommandDisplayMetadata(params.slashCommand, params.dslQuery, slashCommands, params.slashCommandSubmittedLabel),
                collection: collection || null,
                workflowConfig,
                targetThreadAccessLevelOverride: targetThreadAccessLevel,
                newFrontendContextUUID,
                promptSource: params.promptSource || 'user',
                querySource: params.querySource,
                timeFromFirstType: params.timeFromFirstType,
                modelPreferenceOverride: params.modelPreferenceOverride,
                modeConfig: params.modeConfig ?? modeConfig,
                browserAgentAllowOnceFromToggle: params.browserAgentAllowOnceFromToggle,
                forceEnableBrowserAgent: params.forceEnableBrowserAgent,
                enableLearnAboutMe: params.enableLearnAboutMe,
                localWorkspaceDirectories: params.localWorkspaceDirectories,
                // Beside the workspace directories for the same reason: the desktop composer
                // is one component across search and Computer, and it hands its kept-local
                // grants to whichever submitter it was given. Dropped here, the grants were
                // armed by a submit that carried nothing to resolve them with.
                localFileGrants: params.localFileGrants,
                sourcesOverride: params.sourcesOverride,
                shouldRedirectToBackendUUID: !csrRedirect,
                frontendUUID: frontend_uuid,
                ...(params.canonicalPageContext && {
                    canonicalPageContext: params.canonicalPageContext,
                }),
                onThreadSlugReceived: (slug) => {
                    if (!csrRedirect) {
                        return;
                    }
                    const pathname = window.location.pathname;
                    if (getThreadUrl) {
                        // Custom redirect path (e.g. spaces split-screen)
                        if (validateUuid(pathname.split('/').pop())) {
                            router.replace(getThreadUrl(slug), 'Home ask input');
                        }
                    }
                    else {
                        const basePath = routesToComputer ? '/computer/tasks' : '/search';
                        if ((pathname.startsWith(`${router.base}/search/`) ||
                            pathname.startsWith(`${router.base}/computer/`)) &&
                            validateUuid(pathname.split('/').pop())) {
                            // We're replacing the current URL that has frontend context UUID with the new backend UUID
                            router.replace(`${basePath}/${slug}`, 'Home ask input');
                        }
                    }
                },
            });
            if (submitted) {
                onAcceptedSubmit?.(params);
            }
            if (routesToComputer) {
                if (submitted) {
                    markComputerTaskCreated({
                        frontendContextUuid: newFrontendContextUUID,
                        querySource: params.querySource,
                    });
                    // The new task must appear on the next Computer dashboard visit
                    // instead of being served from the cached preloaded query.
                    markComputerDashboardGraphQLCacheStale();
                    refreshAccessDecisionAfterFreeTurnUse();
                }
                else {
                    clearComputerTaskLogicalTiming(newFrontendContextUUID);
                }
            }
            // Only navigate to the interstitial `/search/new/{uuid}` when the
            // submission actually kicked off a stream. If submitQuery was
            // rejected (rate-limit gate, empty query), staying put keeps the
            // user on whatever page they submitted from — no broken empty
            // thread page after they dismiss the paywall modal.
            if (csrRedirect && submitted) {
                const newPath = getThreadUrl
                    ? getThreadUrl(frontend_uuid, { isNewThread: true })
                    : routesToComputer
                        ? `/computer/tasks/${frontend_uuid}`
                        : `/search/new/${frontend_uuid}`;
                router.push(newPath, undefined, 'Home ask input');
            }
            return submitted;
        }
        return true;
    }, [
        onSubmit,
        onAcceptedSubmit,
        redirectToNewThread,
        clearResults,
        submitQuery,
        collection,
        workflowConfig,
        modeConfig,
        targetThreadAccessLevel,
        csrRedirect,
        getThreadUrl,
        router,
        handleSubmitQueryWithMention,
        routesToComputer,
        refreshAccessDecisionAfterFreeTurnUse,
        slashCommands,
    ]);
    // Use a ref to prevent multiple submissions instead of `debounce`--if the
    // dependency array changes we don't want to recreate the debounced function
    // and immediately allow submissions
    const isSubmittingRef = useRef(false);
    const debouncedOnSubmit = useCallback((params) => {
        if (disabled || isSubmittingRef.current)
            return false;
        if (routesToComputer &&
            computerBeforeSubmit({
                isAskDeepResearch: !!askDeepResearchModel && hasDeepResearchPrefill(params.prefills),
            })) {
            return false;
        }
        isSubmittingRef.current = true;
        setTimeout(() => (isSubmittingRef.current = false), QUERY_SUBMIT_DEBOUNCE_MS);
        return handleSubmitQuery(params);
    }, [disabled, handleSubmitQuery, routesToComputer, computerBeforeSubmit, askDeepResearchModel]);
    const [value, json, actions] = useAskInputInstance({
        onSubmit: debouncedOnSubmit,
        immediate: !csrRedirect || !!getThreadUrl,
    });
    const onChange = useDeepResearchChipRemovalAnalytics({
        onChange: actions.onChange,
        searchMode: selectedSearchMode,
        isAskDeepResearch: !!askDeepResearchModel,
    });
    return [value, json, useMemo(() => ({ ...actions, onChange }), [actions, onChange])];
}
//# sourceMappingURL=useAskInput.js.map